import React, { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Button, Table, Space, Tag, Typography, Switch, Modal, message, Breadcrumb, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { INIT_MALL_STRATEGIES, INIT_MALL_CATEGORIES, MALL_SELECTABLE_PACKAGES } from './mallMockData';
import MallCategoryManageDialog from './components/MallCategoryManageDialog';
import MallAddPackageDialog from './components/MallAddPackageDialog';
import { DomesticPrice, OverseasPrice, CostPrice } from './components/PackagePriceCells';

const { Text } = Typography;

const getDirectionText = (d) => (d === 1 ? '设备' : d === 2 ? '用户' : '-');

// ══════════════════════════════════════════════
//  商城位运营 - 套餐配置子页面
//  真实项目：views/operation/mall/packageConfig.vue
// ══════════════════════════════════════════════

const MallPackageConfig = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const strategyId = Number(searchParams.get('id'));
    const strategy = INIT_MALL_STRATEGIES.find(s => s.id === strategyId);

    const [categories, setCategories] = useState(() => JSON.parse(JSON.stringify(INIT_MALL_CATEGORIES)));
    const [categoryDialog, setCategoryDialog] = useState(false);
    const [addPkgDialog, setAddPkgDialog] = useState(false);

    const totalPkgCount = useMemo(
        () => categories.reduce((sum, c) => sum + (c.packages?.length || 0), 0),
        [categories]
    );

    // 分类管理（弹窗只维护分类名称/编码/数量，这里同步到套餐分类结构）
    const handleCategoryChange = (nextList) => {
        setCategories(prev => nextList.map(item => {
            const existing = prev.find(c => c.id === item.id);
            return existing
                ? { ...existing, name: item.name, code: item.code }
                : { id: item.id, name: item.name, code: item.code, packages: [] };
        }));
    };

    // 切换上架状态
    const handleToggleStatus = (categoryId, pkg) => {
        setCategories(prev => prev.map(c => (c.id === categoryId
            ? { ...c, packages: c.packages.map(p => (p.id === pkg.id ? { ...p, status: p.status === 1 ? 2 : 1 } : p)) }
            : c)));
        message.success(pkg.status === 1 ? '下架成功' : '上架成功');
    };

    // 移除套餐
    const handleRemove = (categoryId, pkg) => {
        Modal.confirm({
            title: '移除确认',
            content: `确定要移除套餐"${pkg.packageName}"吗？`,
            okText: '确定', cancelText: '取消', okButtonProps: { danger: true },
            onOk: () => {
                setCategories(prev => prev.map(c => (c.id === categoryId ? { ...c, packages: c.packages.filter(p => p.id !== pkg.id) } : c)));
                message.success('移除成功');
            },
        });
    };

    // 添加套餐确认：全量覆盖
    const handleAddConfirm = (submitCategories) => {
        setCategories(prev => prev.map(category => {
            const submitted = submitCategories.find(s => s.categoryId === category.id);
            if (!submitted) return category;
            const packages = submitted.packageIds.map(pid => {
                const existing = category.packages.find(p => p.packageId === pid);
                if (existing) return existing;
                const src = MALL_SELECTABLE_PACKAGES.find(p => p.packageId === pid);
                return {
                    id: Date.now() + Math.floor(Math.random() * 1000),
                    packageId: pid,
                    packageName: src?.name || pid,
                    applicationDirection: src?.applicationDirection ?? 1,
                    isFree: src?.isFree ?? 0,
                    isSubscription: src?.isSubscription ?? 0,
                    isNoSubscription: src?.isNoSubscription ?? 0,
                    salePriceCny: src?.salePriceCny ?? null,
                    salePriceUsd: src?.salePriceUsd ?? null,
                    noSubscriptionPriceCny: src?.noSubscriptionPriceCny ?? null,
                    noSubscriptionPriceUsd: src?.noSubscriptionPriceUsd ?? null,
                    subscriptionRegionPricing: src?.subscriptionRegionPricing ?? null,
                    noSubscriptionRegionPricing: src?.noSubscriptionRegionPricing ?? null,
                    platformCostCny: src?.platformCostCny ?? null,
                    platformCostUsd: src?.platformCostUsd ?? null,
                    status: 1,
                };
            });
            return { ...category, packages };
        }));
        setAddPkgDialog(false);
        message.success('保存成功');
    };

    const getPackageColumns = (categoryId) => [
        {
            title: '套餐信息', key: 'packageInfo', width: 220,
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontWeight: 600 }}>{record.packageName}</div>
                    <div style={{ fontSize: 12, color: '#909399', fontFamily: 'monospace' }}>{record.packageId}</div>
                </div>
            ),
        },
        { title: '应用方向', key: 'applicationDirection', width: 100, align: 'center', render: (_, r) => getDirectionText(r.applicationDirection) },
        {
            title: '订阅类型', key: 'subscriptionType', width: 180, align: 'center',
            render: (_, r) => {
                if (r.isFree === 1) return <Tag color="green">免费套餐</Tag>;
                if (r.isSubscription === 1 || r.isNoSubscription === 1) {
                    return (
                        <Space size={4} wrap>
                            {r.isSubscription === 1 && <Tag color="blue">订阅制</Tag>}
                            {r.isNoSubscription === 1 && <Tag color="orange">非订阅</Tag>}
                        </Space>
                    );
                }
                return '-';
            },
        },
        {
            title: '国内售价', key: 'cnyPrice', width: 120,
            render: (_, r) => <DomesticPrice pkg={r} />,
        },
        {
            title: '海外售价（区域定价）', key: 'usdPrice', width: 200,
            render: (_, r) => <OverseasPrice pkg={r} />,
        },
        {
            title: '套餐底价', key: 'platformCost', width: 120,
            render: (_, r) => <CostPrice pkg={r} />,
        },
        {
            title: '上架状态', key: 'status', width: 100, align: 'center',
            render: (_, record) => <Switch checked={record.status === 1} onChange={() => handleToggleStatus(categoryId, record)} />,
        },
        {
            title: '操作', key: 'action', width: 100, align: 'center', fixed: 'right',
            render: (_, record) => <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemove(categoryId, record)}>移除</Button>,
        },
    ];

    return (
        <div style={{ padding: 24, background: '#f5f6f8', minHeight: '100vh' }}>
            <Breadcrumb style={{ marginBottom: 16 }} items={[
                { title: '运营管理' },
                { title: <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/operation/mall')}>商城位运营</Button> },
                { title: '套餐配置' },
            ]} />

            <Card>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/operation/mall')}>返回</Button>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>{strategy?.name || '默认策略'}</div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            套餐配置：当前已配置 <Text style={{ color: '#1677ff', fontWeight: 600 }}>{totalPkgCount}</Text> 个套餐，按照可选择分类内排序
                        </Text>
                    </div>
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => setCategoryDialog(true)}>分类管理</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddPkgDialog(true)}>添加套餐</Button>
                    </Space>
                </div>

                {categories.map(category => (
                    <div key={category.id} style={{ marginBottom: 32 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: '#f5f7fa', borderRadius: 4, marginBottom: 16 }}>
                            <AppstoreOutlined style={{ fontSize: 18, color: '#409eff' }} />
                            <span style={{ fontSize: 16, fontWeight: 600, color: '#303133' }}>{category.name}</span>
                            <span style={{ fontSize: 14, color: '#909399', marginLeft: 'auto' }}>{category.packages?.length || 0} 个套餐</span>
                        </div>
                        <Table
                            dataSource={category.packages || []}
                            columns={getPackageColumns(category.id)}
                            rowKey="id"
                            bordered={false}
                            pagination={false}
                            scroll={{ x: 1280 }}
                            size="small"
                        />
                    </div>
                ))}

                {categories.length === 0 && (
                    <Empty description="暂无分类配置">
                        <Button type="primary" icon={<EditOutlined />} onClick={() => setCategoryDialog(true)}>分类管理</Button>
                    </Empty>
                )}
            </Card>

            <MallCategoryManageDialog
                open={categoryDialog}
                categories={categories.map(c => ({ id: c.id, name: c.name, code: c.code, packageCount: c.packages?.length || 0 }))}
                onCancel={() => setCategoryDialog(false)}
                onChange={(nextList) => handleCategoryChange(nextList)}
            />

            <MallAddPackageDialog
                open={addPkgDialog}
                categories={categories}
                onCancel={() => setAddPkgDialog(false)}
                onConfirm={handleAddConfirm}
            />
        </div>
    );
};

export default MallPackageConfig;
